/**
 * Contract Termination API Client
 * US-CONT-006: Terminate Contract
 * Handles all API calls for contract termination
 */

import { authManager } from '@/services/api';
import type {
  TerminateContractRequest,
  TerminateContractResponse,
  TerminationHistory,
  TerminationPermission,
  TerminationApiResponse
} from '../types/contract-termination.types';

/**
 * Get Commerce Service URL from environment
 */
const COMMERCE_SERVICE_URL = import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service';

/**
 * Contract Termination API Client
 */
export const contractTerminationApi = {
  /**
   * Terminate a contract
   * @param request - Termination request payload
   * @returns Promise with termination response
   */
  async terminateContract(request: TerminateContractRequest): Promise<TerminationApiResponse<TerminateContractResponse>> {
    try {
      const token = authManager.getToken();

      const response = await fetch(
        `${COMMERCE_SERVICE_URL}/api/v1/contracts/${request.contractId}/terminate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          },
          body: JSON.stringify({
            reason: request.reason,
            notes: request.notes,
            authorizationCode: request.authorizationCode
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to terminate contract');
      }

      const result = await response.json();
      const data = result.data || result;

      return {
        success: true,
        data: {
          success: true,
          contractId: data.contractId || request.contractId,
          contractNumber: data.contractNumber || '',
          terminationDate: data.terminationDate || new Date().toISOString(),
          terminatedBy: data.terminatedBy || '',
          reason: data.reason || request.reason,
          notes: data.notes || request.notes,
          message: data.message || 'Contract terminated successfully'
        },
        timestamp: data.timestamp || new Date().toISOString()
      };
    } catch (error) {
      console.error('Error terminating contract:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to terminate contract',
        timestamp: new Date().toISOString()
      };
    }
  },

  /**
   * Check if user can terminate a contract
   * @param contractId - Contract ID
   * @returns Promise with permission check result
   */
  async canTerminateContract(contractId: string): Promise<TerminationApiResponse<TerminationPermission>> {
    try {
      const token = authManager.getToken();

      const response = await fetch(
        `${COMMERCE_SERVICE_URL}/api/v1/contracts/${contractId}/can-terminate`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to check termination permission');
      }

      const result = await response.json();
      const data = result.data || result;

      return {
        success: true,
        data: {
          canTerminate: data.canTerminate || false,
          reason: data.reason,
          requiresAuthorization: data.requiresAuthorization || false
        }
      };
    } catch (error) {
      console.error('Error checking termination permission:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to check permission',
        data: {
          canTerminate: false,
          reason: 'Unable to verify permissions'
        }
      };
    }
  },

  /**
   * Get termination history for a contract
   * @param contractId - Contract ID
   * @returns Promise with termination history
   */
  async getTerminationHistory(contractId: string): Promise<TerminationApiResponse<TerminationHistory>> {
    try {
      const token = authManager.getToken();

      const response = await fetch(
        `${COMMERCE_SERVICE_URL}/api/v1/contracts/${contractId}/termination-history`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch termination history');
      }

      const result = await response.json();
      const data = result.data || result;

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Error fetching termination history:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch termination history'
      };
    }
  },

  /**
   * Send termination notification to customer
   * @param contractId - Contract ID
   * @returns Promise with notification result
   */
  async sendTerminationNotification(contractId: string): Promise<TerminationApiResponse<{ sent: boolean; message: string }>> {
    try {
      const token = authManager.getToken();

      const response = await fetch(
        `${COMMERCE_SERVICE_URL}/api/v1/contracts/${contractId}/notify-termination`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send notification');
      }

      const result = await response.json();
      const data = result.data || result;

      return {
        success: true,
        data: {
          sent: data.sent || true,
          message: data.message || 'Notification sent successfully'
        }
      };
    } catch (error) {
      console.error('Error sending termination notification:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send notification',
        data: {
          sent: false,
          message: 'Notification failed'
        }
      };
    }
  }
};

/**
 * Query keys factory for React Query
 * Provides consistent cache key generation
 */
export const terminationQueryKeys = {
  all: ['contract-termination'] as const,
  permission: (contractId: string) => [...terminationQueryKeys.all, 'permission', contractId] as const,
  history: (contractId: string) => [...terminationQueryKeys.all, 'history', contractId] as const
};
